import DesktopMainImg from "@assets/wordpuzzle/Desktop_Main-img_01.svg";
import { twMerge } from "tailwind-merge";

import QuestionCard from "./QuestionCard";
import ResultsSection from "./ResultsSection";
import StatusMessage from "./StatusMessage";

export type Questions = { [key: string]: string }[];

type ResultsProps = {
  isMobile: boolean;
  isQuickNews?: boolean;
  questionNum: number;
  questions: Questions;
  englishWord: string;
  options: string[];
  selectedWord: string | null;
  correctTamilWord: string;
  handleWordClick: (word: string) => void;
  resultVisible: boolean;
  handleQuizNavigation: () => void;
  status: string;
  incorrectCombination: string;
  wordQuizCompleted: boolean;
};

const ProgressBar = ({ current }: { current: number }) => {
  const percentage = (current / 10) * 100;

  return (
    <div className="flex w-full gap-xs items-center font-primary relative -top-2">
      <div className="w-full bg-gray-2050 rounded-[8px] h-[8px]">
        <div
          className="bg-orange-500 h-[8px] rounded-[8px]"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="text-2sm text-pink-500 leading-6 font-bold">
        {current}/10
      </div>
    </div>
  );
};

const Header = ({
  isMobile,
  isQuickNews,
}: {
  isMobile: boolean;
  isQuickNews?: boolean;
}) => (
  <div
    className={twMerge(
      "flex items-center justify-center",
      isQuickNews && "qn-quiz-header flex-col-reverse items-baseline",
    )}
  >
    <img
      src={DesktopMainImg}
      alt="logo"
      width={isMobile ? 157 : 192}
      height={isMobile ? 166 : 172}
      className={twMerge("block px-2 h-auto", isQuickNews && "h-34 w-full")}
    />
    <span
      className={twMerge(
        "font-primary relative",
        isQuickNews && "pb-6 mt-6 grid gap-1",
      )}
    >
      <div className="text-lg md:text-3md font-semibold leading-9 text-pink-900">
        முரசு சொல்லாட்டம்
      </div>
      <div
        className={twMerge(
          "text-gray-150 font-bold md:font-semibold leading-7",
          isMobile ? "text-sm" : "text-2sm",
          isQuickNews && "w-79 text-md",
        )}
      >
        உங்கள் தமிழ் மொழித்திறனைச் சோதிக்கவும்
      </div>
    </span>
  </div>
);

export default function QuestionDetailSection(
  props: ResultsProps,
): JSX.Element {
  const {
    isMobile,
    isQuickNews,
    questionNum,
    questions,
    englishWord,
    options,
    selectedWord,
    correctTamilWord,
    handleWordClick,
    resultVisible,
    handleQuizNavigation,
    status,
    incorrectCombination,
    wordQuizCompleted,
  } = props;

  const questionNumber = wordQuizCompleted ? 10 : questionNum;
  const showQuestion =
    questions.length > 0 && !resultVisible && !wordQuizCompleted;

  const showResults = wordQuizCompleted;

  return (
    <div
      className={twMerge(
        "flex justify-center items-center",
        isMobile ? "flex-col w-full" : "gap-10 bg-gray-500 pb-6",
      )}
    >
      {/* Header Section */}
      <div
        className={twMerge(
          isMobile ? "w-full bg-gray-500 px-sm py-4" : "w-[480px] pt-6",
        )}
      >
        <Header isMobile={isMobile} isQuickNews={isQuickNews} />
        <ProgressBar current={questionNumber} />
      </div>

      {/* Content Section */}
      <div className={twMerge(isMobile && "flex flex-col w-full")}>
        {isMobile && showQuestion && (
          <StatusMessage
            status={status}
            incorrectCombination={incorrectCombination}
            resultsVisible={resultVisible}
          />
        )}

        {showQuestion && (
          <QuestionCard
            englishWord={englishWord}
            options={options}
            selectedWord={selectedWord}
            correctTamilWord={correctTamilWord}
            onWordClick={handleWordClick}
            isMobile={isMobile}
            isQuickNews={isQuickNews}
          />
        )}

        {showResults && (
          <ResultsSection
            handleQuizNavigation={handleQuizNavigation}
            isQuickNews={isQuickNews}
          />
        )}
      </div>
    </div>
  );
}
